import { z } from 'zod';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { RstToPlatformType } from '~/stores/multistream.store';
import { toZodEnum } from '~/lib/forms';

type TFieldKey =
  | 'shared'
  | 'twitch'
  | 'youtube'
  | 'tiktok'
  | 'kick'
  | 'facebook'
  | 'patreon'
  | 'twitter'
  | 'trovo';

interface IFields extends Record<TFieldKey, Record<string, Field>> {
  shared: {
    title: Field;
    description: Field;
  };
}

export const createFileField = (
  options: Omit<WithOptional<FieldFile, 'schema'>, 'type'>,
) => {
  return {
    ...options,
    type: 'file' as const,
    schema:
      options.schema ||
      z.union([
        z.string(),
        z
          .custom<File>(
            (file: File) =>
              !file?.type ||
              ['image/jpeg', 'image/png', 'image/gif'].includes(file.type),
            { message: 'Invalid file type' },
          )
          .refine(
            (file) =>
              !file?.size ||
              (options.maxSize ? file.size <= options.maxSize : false),
            {
              message: `File size exceeds the ${formatBytes(options.maxSize!)} limit`,
            },
          ),
      ]),
  };
};

// Adapted from https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
const formatBytes = (x: number) => {
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  let l = 0,
    n = x || 0;

  while (n >= 1000 && ++l) {
    n = n / 1000;
  }

  return n.toFixed(n < 10 && l > 0 ? 1 : 0) + ' ' + units[l];
};

export const twitchTagSchema = z
  .string()
  .trim()
  .regex(/^[A-Z0-9]+$/i, 'Tags can only contain letters and numbers')
  .max(25, 'Tags must be 25 characters or less');

export const useGoLiveForm = () => {
  const api = useApiStore();
  const multistream = useMultistreamStore();

  const ytInfo = api.golive.youtube.info;
  const twitchInfo = api.golive.twitch.info;
  const fbInfo = api.golive.facebook.info;
  const patreonInfo = api.golive.patreon.info;

  const ytDefaults = {
    privacy: ref<'public' | 'private' | 'unlisted'>('public'),
    latency: ref('normal'),
    category: ref('20'),
  };

  const tiktokDefaults = {
    audience_type: ref<'0' | '1'>('0'),
  };

  const fields: IFields = {
    shared: {
      title: {
        type: 'text',
        name: 'title',
        label: '* Title',
        placeholder: 'Enter a title',
        schema: z.string().min(1).max(140),
      },
      description: {
        type: 'textarea',
        name: 'description',
        label: 'Description',
        placeholder: 'Enter a description',
        schema: z.string().optional(),
      },
    },

    youtube: {
      title: {
        type: 'text',
        name: 'youtube_title',
        label: '* Title',
        placeholder: 'Enter a title',
        schema: z.string().min(1).max(140),
      },
      description: {
        type: 'textarea',
        name: 'youtube_description',
        label: 'Description',
        placeholder: 'Enter a description',
        schema: z.string().optional(),
      },
      scheduled_event: {
        type: 'combo',
        name: 'youtube_broadcast_id',
        label: 'Event',
        placeholder: 'Select an event',
        options: computed(() => {
          const options = [{ value: '0', label: 'Create New Event' }];
          const scheduledStreams = ytInfo.data?.scheduled_streams || [];

          options.push(
            ...scheduledStreams.map(({ id, title }) => ({
              value: id,
              label: title,
            })),
          );

          return options;
        }),

        schema: computed(() => {
          const options = ['0'];
          const scheduledStreams = ytInfo.data?.scheduled_streams || [];

          options.push(...scheduledStreams.map(({ id }) => id));

          return toZodEnum(options)
            .transform((v) => (v === '0' ? undefined : v))
            .default('0');
        }),
      },

      visiblity: {
        type: 'select',
        name: 'youtube_privacy',
        label: 'Privacy',
        placeholder: 'Select a privacy option',
        options: [
          { value: 'public', label: 'Public' },
          { value: 'private', label: 'Private' },
          { value: 'unlisted', label: 'Unlisted' },
        ],
        schema: z
          .enum(['public', 'private', 'unlisted'])
          .default(ytDefaults.privacy.value),
      },

      category: {
        type: 'select',
        name: 'youtube_category',
        label: 'Category',
        placeholder: 'Select a category',
        options: computed(() => {
          if (!ytInfo.data?.categories) return [];

          return ytInfo.data.categories.map(({ id, title }) => ({
            value: id,
            label: title,
          }));
        }),

        schema: computed(() => {
          if (!ytInfo.data?.categories) return z.string().optional();

          return toZodEnum(ytInfo.data.categories.map(({ id }) => id)).default(
            ytDefaults.category.value,
          );
        }),
      },

      latency: {
        type: 'select',
        name: 'youtube_latency',
        label: 'Stream Latency',
        placeholder: 'Select a latency',
        options: computed(() => {
          if (!ytInfo.data?.settings) return [];

          return Object.entries(ytInfo.data.settings.latency).map(
            ([value, label]) => ({ value, label }),
          );
        }),
        schema: computed(() => {
          if (!ytInfo.data?.settings) {
            return z.string().optional();
          }

          return toZodEnum(Object.keys(ytInfo.data.settings.latency)).default(
            ytDefaults.latency.value,
          );
        }),
      },

      thumbnail: createFileField({
        name: 'youtube_thumbnail',
        label: 'Thumbnail',
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif'],
        // 2 MB, ref: https://developers.google.com/youtube/v3/docs/thumbnails/set
        maxSize: 2000000,
      }),

      options: {
        label: 'Options',
        type: 'checkbox_group',
        fields: [
          {
            type: 'checkbox',
            name: 'youtube_auto_start',
            label: 'Enable auto-start',
            schema: z.boolean().optional().default(true),
          },
          {
            type: 'checkbox',
            name: 'youtube_auto_stop',
            label: 'Enable auto-stop',
            schema: z.boolean().optional().default(true),
          },
          {
            type: 'checkbox',
            name: 'youtube_dvr',
            label: 'Enable DVR',
            schema: z.boolean().optional().default(true),
          },

          {
            type: 'checkbox',
            name: 'youtube_360_video',
            label: '360° Video',
            schema: z.boolean().optional().default(false),
          },

          {
            type: 'checkbox',
            name: 'youtube_for_kids',
            label: 'Made for kids',
            schema: z.boolean().optional().default(false),
          },
        ],
      },
    },
    twitch: {
      title: {
        type: 'text',
        name: 'twitch_title',
        label: '* Title',
        placeholder: 'Enter a title',
        schema: z.string().min(1).max(140),
      },
      category: {
        type: 'twitch_category',
        name: 'twitch_category',
        label: '* Category',
        schema: z.string().optional(),
      },
      tags: {
        type: 'twitch_tags',
        name: 'twitch_tags',
        label: 'Tags',
        schema: z.array(twitchTagSchema).max(10).default([]),
      },
      contentClassification: {
        type: 'combo_box_tags',
        name: 'twitch_content_classification',
        label: 'Content Classification',
        placeholder: 'Select a content classification',
        schema: z.array(z.string()).optional().default([]),
        options: computed(() => {
          return (
            twitchInfo.data?.content_classifications?.map(({ key, label }) => ({
              value: key,
              label,
            })) || []
          );
        }),
      },
      featuresBrandedContent: {
        label: '',
        type: 'checkbox_group',
        fields: [
          {
            type: 'checkbox',
            name: 'twitch_branded_content',
            label: 'Stream features branded content',
            schema: z.boolean().optional().default(false),
          },
        ],
      },
    },
    tiktok: {
      title: {
        type: 'text',
        name: 'tiktok_title',
        label: 'Title',
        placeholder: 'Enter a title',
        schema: z.string().min(1).max(140),
      },
      category: {
        type: 'tiktok_category',
        name: 'tiktok_category',
        label: 'Category',
        schema: z.string().optional(),
      },
      audience_type: {
        type: 'radio_group',
        orientation: 'horizontal',
        name: 'tiktok_audience_type',
        label: 'Audience Type',
        schema: z
          .enum(['0', '1'])
          .default(tiktokDefaults.audience_type.value ?? '0'),
        defaultValue: '0',
        options: [
          { value: '0', label: 'Everyone' },
          { value: '1', label: 'Adult Only' },
        ],
      },
    },
    kick: {
      title: {
        type: 'text',
        name: 'kick_title',
        label: '* Title',
        placeholder: 'Enter a title',
        schema: z.string().min(1).max(140),
      },
      category: {
        type: 'kick_category',
        name: 'kick_category',
        label: 'Category',
        schema: z.string().optional(),
      },
    },
    facebook: {
      title: {
        type: 'text',
        name: 'facebook_title',
        label: '* Title',
        placeholder: 'Enter a title',
        schema: z.string().min(1).max(140),
      },
      description: {
        type: 'textarea',
        name: 'facebook_description',
        label: 'Description',
        placeholder: 'Enter a description',
        schema: z.string().optional(),
      },
      // TODO: we need to add privacy, that only should show if streaming to Timeline
      destination: {
        type: 'select',
        name: 'facebook_destination',
        label: 'Destination',
        placeholder: 'Select where to stream to',
        options: computed(() => {
          const timeline = {
            value: fbInfo.data?.facebook_id || 'me',
            label: 'Timeline',
          };

          const pages =
            fbInfo.data?.pages?.map((page) => ({
              value: page.id,
              label: page.name,
            })) || [];

          return [timeline, ...pages];
        }),
        schema: computed(() => {
          if (!fbInfo.data?.pages) {
            return z.string().optional();
          }

          return toZodEnum([
            fbInfo.data.facebook_id,
            ...fbInfo.data.pages.map((p) => p.id.toString()),
          ]).default(fbInfo.data.facebook_id);
        }),
      },
    },
    twitter: {
      title: {
        type: 'text',
        name: 'twitter_title',
        label: '* Title',
        placeholder: 'Enter a title',
        schema: z.string().min(1).max(140),
      },
    },
    trovo: {
      title: {
        type: 'text',
        name: 'trovo_title',
        label: '* Title',
        placeholder: 'Enter a title',
        schema: z.string().min(1).max(140),
      },
    },
    patreon: {
      title: {
        type: 'text',
        name: 'patreon_title',
        label: '* Title',
        placeholder: 'Enter a title',
        schema: z.string().min(1).max(140),
      },
      description: {
        type: 'textarea',
        name: 'patreon_description',
        label: 'Description',
        placeholder: 'Enter a description',
        schema: z.string().optional(),
      },
      access_rules: {
        type: 'combo_box_tags',
        name: 'patreon_access_rules',
        label: 'Audience',
        placeholder: 'Enter who can watch this',
        options: computed(() => {
          if (!patreonInfo.data?.accessRules) return [];
          return patreonInfo.data.accessRules.map((rule) => ({
            label: rule.value,
            value: rule.key,
          }));
        }),
        schema: z.array(z.string()).optional().default([]),
      },
    },
  };

  const fieldsets = computed<undefined | Fieldset[]>(() => {
    if (multistream.enabledDestinations.length === 0) {
      return;
    }

    if (multistream.enabledDestinations.length > 1) {
      const fieldsets: Fieldset[] = [
        {
          label: 'Shared Settings',
          fields: [fields.shared.title, fields.shared.description],
        },
      ];

      if (
        multistream.enabledPlatforms.includes(RstToPlatformType.tiktok_account)
      ) {
        const { title, description, ...other } = fields.tiktok;
        fieldsets.push({
          label: 'TikTok Settings',
          fields: Object.values(other),
        });
      }

      if (
        multistream.enabledPlatforms.includes(RstToPlatformType.twitch_account)
      ) {
        const { title, description, ...other } = fields.twitch;
        fieldsets.push({
          label: 'Twitch Settings',
          fields: Object.values(other),
        });
      }

      if (
        multistream.enabledPlatforms.includes(RstToPlatformType.kick_account)
      ) {
        const { title, description, ...other } = fields.kick;
        fieldsets.push({
          label: 'Kick Settings',
          fields: Object.values(other),
        });
      }

      if (
        multistream.enabledPlatforms.includes(RstToPlatformType.youtube_account)
      ) {
        const { title, description, ...other } = fields.youtube;
        fieldsets.push({
          label: 'YouTube Settings',
          fields: Object.values(other),
        });
      }

      if (
        multistream.enabledPlatforms.includes(
          RstToPlatformType.facebook_account,
        )
      ) {
        const { title, description, ...other } = fields.facebook;
        fieldsets.push({
          label: 'Facebook Settings',
          fields: Object.values(other),
        });
      }

      if (
        multistream.enabledPlatforms.includes(RstToPlatformType.patreon_account)
      ) {
        const { title, description, ...other } = fields.patreon;
        fieldsets.push({
          label: 'Patreon Settings',
          fields: Object.values(other),
        });
      }

      return fieldsets;
    }

    const destination = multistream.enabledDestinations[0];

    if (destination?.type !== 'platform') {
      return;
    }

    switch (destination.platform_type) {
      case 'twitch':
        return [
          {
            label: 'Twitch Settings',
            fields: Object.values(fields.twitch),
          },
        ];

      case 'tiktok':
        return [
          {
            label: 'TikTok Settings',
            fields: Object.values(fields.tiktok),
          },
        ];

      case 'youtube':
        return [
          {
            label: 'YouTube Settings',
            fields: Object.values(fields.youtube),
          },
        ];
      case 'facebook':
        return [
          {
            label: 'Facebook Settings',
            fields: Object.values(fields.facebook),
          },
        ];
      case 'kick':
        return [
          {
            label: 'Kick Settings',
            fields: Object.values(fields.kick),
          },
        ];
      case 'twitter':
        return [
          {
            label: 'X (Twitter) Settings',
            fields: Object.values(fields.twitter),
          },
        ];
      case 'trovo':
        return [
          {
            label: 'Trovo Settings',
            fields: Object.values(fields.trovo),
          },
        ];
      case 'patreon':
        return [
          {
            label: 'Patreon Settings',
            fields: Object.values(fields.patreon),
          },
        ];
    }
  });

  const form = useForm({
    validationSchema: computed(() => {
      const schemas: Record<string, any> = {
        destinationCount: z
          .number()
          .gt(0, { message: 'At least one destination is required' }),
      };

      for (const fieldset of fieldsets.value || []) {
        for (const field of fieldset.fields) {
          if (field.type === 'checkbox_group') {
            for (const subfield of field.fields) {
              schemas[subfield.name] = unref(subfield.schema);
            }
            continue;
          }

          schemas[field.name] = unref(field.schema);
        }
      }

      return toTypedSchema(z.object(schemas));
    }),
  });

  watch(
    () => form.values.youtube_broadcast_id,
    (v) => {
      const stream = ytInfo.data?.scheduled_streams?.find(
        (stream) => stream.id === v,
      );

      if (stream) {
        form.setValues({
          youtube_title: stream.title,
          youtube_description: stream.description,
          youtube_category: stream.category_id,
          youtube_privacy: stream.privacy,
          youtube_thumbnail: stream.thumbnail.url,
          youtube_auto_start: stream.auto_start,
          youtube_auto_stop: stream.auto_stop,
          youtube_dvr: stream.dvr,
          youtube_360_video: stream.projection === '360',
          youtube_for_kids: stream.for_kids,
        });
      }
    },
  );

  return { fieldsets, fields, form };
};
